/**
 * Compose une formule LaTeX en texte Unicode lisible, pour le rendu NATIF.
 *
 * Pourquoi pas KaTeX : KaTeX produit du HTML+CSS, donc une WebView. Or une
 * conversation affiche N bulles à la fois et défile — `StructuredCorrectionBlock`
 * documente déjà que la parade au coût d'une WebView est de n'en monter qu'UNE
 * (accordéon), ce qui ne se transpose pas à une messagerie.
 *
 * Le pari inverse : pour des maths de collège — fractions, puissances,
 * racines, symboles — un rendu Unicode dans un `<Text>` natif est meilleur
 * qu'une WebView. Il s'écoule dans la phrase, se coupe en fin de ligne, se
 * sélectionne, suit le thème, coûte zéro et défile parfaitement.
 *
 * Ce qui dépasse ce cadre dégrade en une forme linéaire lisible plutôt que de
 * disparaître : `\oint_C` reste `\oint_C`, ce qui est laid mais honnête, là où
 * un rendu partiel serait faux.
 */

const SYMBOLS = {
    "\\times": "×", "\\div": "÷", "\\pm": "±", "\\mp": "∓", "\\cdot": "⋅",
    "\\ast": "∗", "\\star": "⋆", "\\circ": "∘", "\\bullet": "•",
    "\\le": "≤", "\\leq": "≤", "\\ge": "≥", "\\geq": "≥",
    "\\ne": "≠", "\\neq": "≠", "\\equiv": "≡", "\\approx": "≈", "\\sim": "∼",
    "\\ll": "≪", "\\gg": "≫", "\\propto": "∝",
    "\\infty": "∞", "\\partial": "∂", "\\nabla": "∇",
    "\\alpha": "α", "\\beta": "β", "\\gamma": "γ", "\\delta": "δ",
    "\\epsilon": "ε", "\\varepsilon": "ε", "\\zeta": "ζ", "\\eta": "η",
    "\\theta": "θ", "\\lambda": "λ", "\\mu": "μ", "\\nu": "ν", "\\xi": "ξ",
    "\\pi": "π", "\\rho": "ρ", "\\sigma": "σ", "\\tau": "τ", "\\phi": "φ",
    "\\varphi": "φ", "\\chi": "χ", "\\psi": "ψ", "\\omega": "ω",
    "\\Gamma": "Γ", "\\Delta": "Δ", "\\Theta": "Θ", "\\Lambda": "Λ",
    "\\Pi": "Π", "\\Sigma": "Σ", "\\Phi": "Φ", "\\Psi": "Ψ", "\\Omega": "Ω",
    "\\rightarrow": "→", "\\to": "→", "\\leftarrow": "←", "\\gets": "←",
    "\\Rightarrow": "⇒", "\\Leftarrow": "⇐", "\\Leftrightarrow": "⇔",
    "\\leftrightarrow": "↔", "\\mapsto": "↦",
    "\\in": "∈", "\\notin": "∉", "\\subset": "⊂", "\\subseteq": "⊆",
    "\\supset": "⊃", "\\cup": "∪", "\\cap": "∩", "\\emptyset": "∅",
    "\\varnothing": "∅", "\\forall": "∀", "\\exists": "∃",
    "\\mathbb{N}": "ℕ", "\\mathbb{Z}": "ℤ", "\\mathbb{Q}": "ℚ",
    "\\mathbb{R}": "ℝ", "\\mathbb{C}": "ℂ",
    "\\sum": "∑", "\\prod": "∏", "\\int": "∫", "\\sqrt": "√",
    "\\angle": "∠", "\\perp": "⊥", "\\parallel": "∥", "\\degree": "°",
    "\\ldots": "…", "\\dots": "…", "\\cdots": "⋯",
    "\\%": "%", "\\&": "&", "\\_": "_", "\\{": "{", "\\}": "}", "\\#": "#",
    "\\left": "", "\\right": "", "\\displaystyle": "", "\\limits": "",
    "\\,": " ", "\\;": " ", "\\:": " ", "\\!": "", "\\ ": " ",
    "\\quad": "  ", "\\qquad": "    ", "\\\\": "\n",
}

const SUPERSCRIPT = {
    0: "⁰", 1: "¹", 2: "²", 3: "³", 4: "⁴", 5: "⁵", 6: "⁶", 7: "⁷", 8: "⁸",
    9: "⁹", "+": "⁺", "-": "⁻", "−": "⁻", "=": "⁼", "(": "⁽", ")": "⁾",
    n: "ⁿ", i: "ⁱ", x: "ˣ", y: "ʸ", a: "ᵃ", b: "ᵇ", c: "ᶜ", d: "ᵈ", k: "ᵏ",
    m: "ᵐ", p: "ᵖ", t: "ᵗ", " ": " ",
}

const SUBSCRIPT = {
    0: "₀", 1: "₁", 2: "₂", 3: "₃", 4: "₄", 5: "₅", 6: "₆", 7: "₇", 8: "₈",
    9: "₉", "+": "₊", "-": "₋", "−": "₋", "=": "₌", "(": "₍", ")": "₎",
    a: "ₐ", e: "ₑ", h: "ₕ", i: "ᵢ", j: "ⱼ", k: "ₖ", l: "ₗ", m: "ₘ", n: "ₙ",
    o: "ₒ", p: "ₚ", r: "ᵣ", s: "ₛ", t: "ₜ", u: "ᵤ", v: "ᵥ", x: "ₓ", " ": " ",
}

// Les fractions usuelles ont leur glyphe : ¾ se lit mieux que 3/4 dans une
// phrase, et c'est la notation que l'élève voit dans son manuel.
const VULGAR = {
    "1/2": "½", "1/3": "⅓", "2/3": "⅔", "1/4": "¼", "3/4": "¾",
    "1/5": "⅕", "2/5": "⅖", "3/5": "⅗", "4/5": "⅘", "1/6": "⅙", "5/6": "⅚",
    "1/7": "⅐", "1/8": "⅛", "3/8": "⅜", "5/8": "⅝", "7/8": "⅞",
    "1/9": "⅑", "1/10": "⅒",
}

const isCompound = (s) => !/^[-−]?[\wπ°]+$/u.test(s)
const wrap = (s) => (isCompound(s) ? `(${s})` : s)

const toScript = (s, table) => {
    const out = []
    for (const ch of s) {
        if (table[ch] === undefined) return null
        out.push(table[ch])
    }
    return out.join("")
}

/** Lit un groupe `{…}` (ou le caractère seul) à partir de `i`. */
function readGroup(src, i) {
    if (src[i] !== "{") {
        // `x^2` : l'argument est le seul caractère qui suit.
        if (src[i] === "\\") {
            const m = /^\\[a-zA-Z]+/.exec(src.slice(i))
            if (m) return { content: m[0], next: i + m[0].length }
        }
        return { content: src[i] ?? "", next: i + 1 }
    }
    let depth = 0
    for (let j = i; j < src.length; j++) {
        if (src[j] === "{" && src[j - 1] !== "\\") depth += 1
        else if (src[j] === "}" && src[j - 1] !== "\\") {
            depth -= 1
            if (depth === 0) {
                return { content: src.slice(i + 1, j), next: j + 1 }
            }
        }
    }
    // Accolade jamais refermée : on prend le reste plutôt que de boucler.
    return { content: src.slice(i + 1), next: src.length }
}

/**
 * @param {string} latex
 * @returns {string} la formule composée en Unicode
 */
export function latexToUnicode(latex) {
    if (!latex || typeof latex !== "string") return ""
    let out = ""
    let i = 0

    while (i < latex.length) {
        const rest = latex.slice(i)

        if (rest.startsWith("\\frac") || rest.startsWith("\\dfrac") || rest.startsWith("\\tfrac")) {
            const afterName = i + (rest.startsWith("\\frac") ? 5 : 6)
            const num = readGroup(latex, afterName)
            const den = readGroup(latex, num.next)
            const a = latexToUnicode(num.content)
            const b = latexToUnicode(den.content)
            out += VULGAR[`${a}/${b}`] || `${wrap(a)}/${wrap(b)}`
            i = den.next
            continue
        }

        if (rest.startsWith("\\sqrt")) {
            let j = i + 5
            let index = ""
            if (latex[j] === "[") {
                const close = latex.indexOf("]", j)
                if (close !== -1) {
                    index = latex.slice(j + 1, close)
                    j = close + 1
                }
            }
            const arg = readGroup(latex, j)
            const inner = latexToUnicode(arg.content)
            const root = index === "3" ? "∛" : index === "4" ? "∜" : "√"
            // Racine sans radicande : Sam écrit « on l'écrit $\sqrt{\,}$,
            // puis on met le nombre dedans ». Le signe seul, pas « √() ».
            out += inner ? `${root}${wrap(inner)}` : root
            i = arg.next
            continue
        }

        if (rest.startsWith("\\text") || rest.startsWith("\\mathrm") || rest.startsWith("\\mathbf")) {
            const nameLen = rest.startsWith("\\text") ? 5 : 7
            const arg = readGroup(latex, i + nameLen)
            out += arg.content
            i = arg.next
            continue
        }

        if (latex[i] === "^" || latex[i] === "_") {
            const isSup = latex[i] === "^"
            const arg = readGroup(latex, i + 1)
            const inner = latexToUnicode(arg.content)
            const scripted = toScript(inner, isSup ? SUPERSCRIPT : SUBSCRIPT)
            // Pas de glyphe pour tous les caractères : on garde la forme
            // linéaire, lisible, plutôt qu'un mélange bancal.
            out += scripted ?? `${latex[i]}${wrap(inner)}`
            i = arg.next
            continue
        }

        if (latex[i] === "\\") {
            const macro = /^\\(?:[a-zA-Z]+|.)/.exec(rest)?.[0]
            // `\mathbb{R}` et consorts : la clé porte son argument.
            const withArg = macro && /^\\math(bb|cal|frak)$/.test(macro)
            if (withArg) {
                const arg = readGroup(latex, i + macro.length)
                const key = `${macro}{${arg.content}}`
                if (SYMBOLS[key] !== undefined) {
                    out += SYMBOLS[key]
                    i = arg.next
                    continue
                }
            }
            if (macro && SYMBOLS[macro] !== undefined) {
                out += SYMBOLS[macro]
                i += macro.length
                continue
            }
            // Macro inconnue : laissée telle quelle. Laid mais honnête — un
            // rendu partiel serait faux, et faux en maths est pire que laid.
            out += macro ?? latex[i]
            i += macro ? macro.length : 1
            continue
        }

        if (latex[i] === "{" || latex[i] === "}") {
            i += 1
            continue
        }

        out += latex[i]
        i += 1
    }

    return out.replace(/[ \t]{2,}/g, " ").trim()
}

// Noms de commandes connus, pour réparer un antislash oublié. Les noms de
// moins de trois lettres sont écartés (`ne`, `le`, `in`, `to`…) : ce sont des
// mots français courants, et les restaurer ferait plus de dégâts que de bien.
// `pi` fait exception — c'est celui que le modèle écrit nu le plus souvent.
const MACRO_NAMES = new Set([
    ...Object.keys(SYMBOLS)
        .filter((k) => /^\\[a-zA-Z]{3,}$/.test(k))
        .map((k) => k.slice(1)),
    "pi",
    "frac", "dfrac", "tfrac", "sqrt", "text", "mathrm", "mathbf", "mathbb",
])

// Le caractère qu'a produit l'échappement JSON, et la lettre qu'il a mangée.
// `\times` écrit sans doubler l'antislash devient une tabulation suivie de
// « imes » : c'est `\t`. Le service IA répare à la source, mais les
// conversations déjà en cache portent la mutilation. `\n` et `\r` sont exclus —
// un vrai saut de ligne est légitime, on ne peut pas l'en distinguer.
const ESCAPE_VICTIMS = { "\t": "t", "\f": "f", "\b": "b" }
// eslint-disable-next-line no-control-regex
const MUTILATED_MACRO = /([\t\f\x08])([a-zA-Z]+)/g

/**
 * Reconstitue une commande à partir du caractère de contrôle qu'en a fait
 * l'échappement JSON.
 *
 * @param {string} text
 * @returns {string}
 */
export function restoreMutilatedMacros(text) {
    if (!text || typeof text !== "string") return text
    return text.replace(MUTILATED_MACRO, (match, victim, tail) => {
        const letter = ESCAPE_VICTIMS[victim]
        // Du plus long au plus court : `\theta` avant `\to`, et ce qui suit la
        // commande reste du texte.
        for (let cut = tail.length; cut > 0; cut -= 1) {
            const name = letter + tail.slice(0, cut)
            if (MACRO_NAMES.has(name)) return `\\${name}${tail.slice(cut)}`
        }
        return match
    })
}

/**
 * Restaure l'antislash d'une commande que le modèle a écrite nue.
 *
 * Observé en recette, sur la même conversation que le LaTeX hors
 * délimiteurs : `$A = pi \times r^2$`, `$sqrt{9}$`. Entre des `$`, un mot qui
 * porte un nom de commande connu EST cette commande — personne ne nomme une
 * variable `sqrt`.
 *
 * @param {string} source le contenu d'un segment mathématique
 * @returns {string}
 */
export function repairBareMacros(source) {
    if (!source || typeof source !== "string") return source
    // `\text{…}` contient de la prose française, où « angle » ou « circ »
    // seraient de vrais mots. Et un modèle qui écrit `\text` sait poser des
    // antislashs : il n'a pas besoin du filet.
    if (/\\(text|mathrm|mathbf)/.test(source)) return source
    return source.replace(/(^|[^\\a-zA-Z])([a-zA-Z]+)/g, (match, before, word) =>
        MACRO_NAMES.has(word) ? `${before}\\${word}` : match
    )
}

// Une suite d'« atomes » mathématiques : une commande avec ses arguments, un
// opérande court avec son éventuel exposant, un opérateur. `*` en est exclu :
// c'est le gras Markdown, pas une multiplication.
const CMD = "\\\\[a-zA-Z]+(?:\\[[^\\]]*\\])?(?:\\{[^{}]*\\})*"
const OPERAND = "[A-Za-z0-9]{1,2}(?:[_^](?:\\{[^{}]*\\}|[A-Za-z0-9-]))?"
const OPERATOR = "[-+=<>/()]"
const ATOM = `(?:${CMD}|${OPERAND}|${OPERATOR})`
const LOOSE_RUN = new RegExp(`${ATOM}(?: ?${ATOM})*`, "g")

/**
 * Rattrape une formule que le modèle a écrite SANS délimiteurs.
 *
 * Observé en recette : sur une même conversation, Sam écrit tantôt
 * `$\pi \times r^2$`, tantôt `A = \pi \times r^2` tout court. Le second cas
 * n'est pas une formule pour l'analyseur, et l'élève lit des antislashs.
 * Le prompt demande les délimiteurs ; ceci est le filet quand le modèle les
 * oublie quand même.
 *
 * On ne recompose que des suites qui contiennent au moins une commande LaTeX.
 * Capturer au passage un mot ordinaire est sans effet : hors commandes, la
 * composition est l'identité.
 *
 * @param {string} text prose, hors de tout délimiteur mathématique
 * @returns {string}
 */
export function rescueLooseLatex(text) {
    if (!text || typeof text !== "string" || !text.includes("\\")) return text
    return text.replace(LOOSE_RUN, (run) =>
        run.includes("\\") ? latexToUnicode(run) : run
    )
}
